// @ts-nocheck
import type { Question } from '../types';
import { QuestionSelector } from './questionSelector';
import { questionMapper } from './questionMapper';
import type { UserProgress, TestGenerationRequest } from './questionSelector';
import { generateMultilingualAIExamQuestions, hasCompleteAIExamTranslation } from '../services/aiExamTranslationService';
import type { Language } from '../types_i18n';

export interface AIExamConfig {
  maxTopics: number;
  questionsPerTopic: number;
  totalQuestions: number;
  difficulty: 'adaptive' | 'mixed';
  categoryWeights: {
    insight: number;
    hazardPerception: number;
    knowledge: number;
  };
}

export interface AIExamResult {
  id: string;
  questions: Question[];
  topics: string[];
  categories: string[];
  difficulty: string;
  estimatedTime: number;
  coveragePercentage: number;
  adaptiveWeighting: boolean;
}

export class AIExamAdapter {
  private questionSelector: QuestionSelector;
  private config: AIExamConfig;

  constructor() {
    const mapping = questionMapper.getMapping();
    this.questionSelector = new QuestionSelector(
      mapping.insight,
      mapping.hazardPerception,
      mapping.knowledge,
      mapping.archive
    );

    this.config = {
      maxTopics: 3,
      questionsPerTopic: 17, // ~50 questions total
      totalQuestions: 50,
      difficulty: 'adaptive',
      categoryWeights: {
        insight: 0.4,
        hazardPerception: 0.35,
        knowledge: 0.25
      }
    };
  }

  // Main AI Exam Generation with Multilingual Support.
  // Not used by AI Exam — Part 1 (buildAIExamQuestions in AIExam.utils.ts) replaces this.
  // Kept for future use.
  generateAIExam(userProgress: UserProgress[], weakAreas: string[] = [], language: Language = 'en'): AIExamResult {
    // 1. Identify top 3 topics based on user performance and weak areas
    const selectedTopics = this.selectTopTopics(userProgress, weakAreas);
    
    // 2. Generate questions from selected topics with adaptive difficulty
    const questions = this.generateAdaptiveQuestions(selectedTopics, userProgress);
    
    // 3. Apply multilingual translations
    const multilingualQuestions = this.applyMultilingualTranslations(questions, language);
    
    // 4. Calculate exam metadata
    const categories = this.extractCategories(selectedTopics);
    const coverage = this.calculateCoverage(selectedTopics);
    const difficulty = this.calculateAdaptiveDifficulty(userProgress);

    return {
      id: `ai-exam-${Date.now()}`,
      questions: multilingualQuestions,
      topics: selectedTopics,
      categories,
      difficulty,
      estimatedTime: this.config.totalQuestions * 1.2, // 1.2 minutes per question
      coveragePercentage: coverage,
      adaptiveWeighting: true
    };
  }

  // Apply multilingual translations to AI Exam questions
  private applyMultilingualTranslations(questions: Question[], language: Language): Question[] {
    return questions.map((question, index) => {
      const questionId = `ai-mixed-${(index % 5) + 1}`; // Cycle through our 5 AI exam templates
      
      if (hasCompleteAIExamTranslation(questionId)) {
        return generateMultilingualAIExamQuestions(questionId, language, question);
      }
      
      // Fallback to original question with language-specific text
      return {
        ...question,
        text: this.translateQuestionText(question.text, language),
        options: question.options.map(option => ({
          ...option,
          text: this.translateOptionText(option.text, language)
        })),
        explanation: this.translateExplanationText(question.explanation, language)
      };
    });
  }

  // Helper methods for text translation
  private translateQuestionText(text: string | any, language: Language): string {
    if (typeof text === 'string') {
      return text; // Return as-is for now, could be enhanced with translation service
    }
    return (text as any)[language] || (text as any).en || text;
  }

  private translateOptionText(text: string | any, language: Language): string {
    if (typeof text === 'string') {
      return text; // Return as-is for now, could be enhanced with translation service
    }
    return (text as any)[language] || (text as any).en || text;
  }

  private translateExplanationText(text: string | any, language: Language): string {
    if (typeof text === 'string') {
      return text; // Return as-is for now, could be enhanced with translation service
    }
    return (text as any)[language] || (text as any).en || text;
  }

  private selectTopTopics(userProgress: UserProgress[], weakAreas: string[]): string[] {
    const topicScores = new Map<string, number>();

    // Calculate scores for all topics based on user performance
    userProgress.forEach(progress => {
      let score = 0;
      
      // Base score from mastery level (inverse - lower mastery = higher priority)
      score += (100 - progress.masteryLevel) * 0.4;
      
      // Weak area bonus
      if (weakAreas.includes(progress.topicId)) {
        score += 30;
      }
      
      // Recency bonus (recently practiced topics get lower priority)
      const daysSinceLastTest = (Date.now() - progress.lastTestDate.getTime()) / (1000 * 60 * 60 * 24);
      score += Math.min(daysSinceLastTest * 2, 20);
      
      // Question completion penalty (topics with many seen questions get lower priority)
      const completionRate = progress.questionsSeen.length / 200; // Assuming ~200 questions per topic max
      score -= completionRate * 15;
      
      topicScores.set(progress.topicId, score);
    });

    // Sort topics by score (highest priority first)
    const sortedTopics = Array.from(topicScores.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, this.config.maxTopics)
      .map(([topicId]) => topicId);

    // If we don't have enough topics from user progress, add default high-value topics
    if (sortedTopics.length < this.config.maxTopics) {
      const defaultTopics = this.getDefaultTopics(sortedTopics);
      sortedTopics.push(...defaultTopics);
    }

    return sortedTopics.slice(0, this.config.maxTopics);
  }

  private getDefaultTopics(excludeTopics: string[]): string[] {
    const highValueTopics = [
      'priorityRules',
      'hazardPerception', 
      'speedLimits',
      'roadSigns',
      'emergencyProcedures'
    ];

    return highValueTopics
      .filter(topic => !excludeTopics.includes(topic))
      .slice(0, this.config.maxTopics - excludeTopics.length);
  }

  private generateAdaptiveQuestions(selectedTopics: string[], userProgress: UserProgress[]): Question[] {
    const allQuestions: Question[] = [];
    const questionsPerTopic = Math.floor(this.config.totalQuestions / selectedTopics.length);

    selectedTopics.forEach(topicId => {
      // Get user progress for this topic
      const topicProgress = userProgress.find(p => p.topicId === topicId);
      
      // Generate test request for this topic
      const testRequest: TestGenerationRequest = {
        category: 'mixed', // We'll let the selector handle category weighting
        topicIds: [topicId],
        userProgress,
        testNumber: 1,
        difficulty: this.calculateTopicDifficulty(topicProgress)
      };

      // Generate questions for this topic
      const testResult = this.questionSelector.generateTest(testRequest);
      
      // Filter questions for this specific topic and limit count
      const topicQuestions = testResult.questions
        .filter(q => this.isQuestionFromTopic(q, topicId))
        .slice(0, questionsPerTopic);

      allQuestions.push(...topicQuestions);
    });

    // Ensure we have exactly the right number of questions
    return allQuestions.slice(0, this.config.totalQuestions);
  }

  private isQuestionFromTopic(question: Question, topicId: string): boolean {
    // This is a simplified check - in practice, you'd have better topic mapping
    const subjectMappings: Record<string, string[]> = {
      'priorityRules': ['Priority', 'Right of Way'],
      'hazardPerception': ['Hazard', 'Danger', 'Risk'],
      'speedLimits': ['Speed', 'Velocity'],
      'roadSigns': ['Sign', 'Marking'],
      'emergencyProcedures': ['Emergency', 'First Aid', 'Accident']
    };

    const keywords = subjectMappings[topicId] || [];
    return keywords.some(keyword => 
      question.subject.includes(keyword) || 
      question.text.includes(keyword)
    );
  }

  private calculateTopicDifficulty(topicProgress?: UserProgress): 'easy' | 'medium' | 'hard' {
    if (!topicProgress) return 'medium';
    
    if (topicProgress.masteryLevel < 40) return 'easy';
    if (topicProgress.masteryLevel < 70) return 'medium';
    return 'hard';
  }

  private extractCategories(selectedTopics: string[]): string[] {
    const categories = new Set<string>();
    
    selectedTopics.forEach(topicId => {
      if (['priorityRules', 'mandatorySigns', 'roadSigns', 'weatherVisibility', 'trafficLights', 'parkingRules'].includes(topicId)) {
        categories.add('insight');
      } else if (['hazardPerception', 'bicycleInteractions', 'pedestrianCrossings', 'tramInteractions', 'emergencyProcedures'].includes(topicId)) {
        categories.add('hazardPerception');
      } else {
        categories.add('knowledge');
      }
    });

    return Array.from(categories);
  }

  private calculateCoverage(selectedTopics: string[]): number {
    // Calculate what percentage of the 600-question pool this covers
    const totalTopics = 15; // Total topics in the active pool
    const coveragePercentage = (selectedTopics.length / totalTopics) * 100;
    
    // Weight by importance - top 3 topics should cover ~80% of critical content
    return Math.min(coveragePercentage * 2.5, 80); // Cap at 80%
  }

  private calculateAdaptiveDifficulty(userProgress: UserProgress[]): string {
    const avgMastery = userProgress.reduce((sum, p) => sum + p.masteryLevel, 0) / userProgress.length;
    
    if (avgMastery < 40) return 'Easy (Focus on fundamentals)';
    if (avgMastery < 70) return 'Medium (Balanced practice)';
    return 'Hard (Advanced scenarios)';
  }

  // Get AI Exam statistics
  getAIExamStats(): any {
    return {
      maxTopics: this.config.maxTopics,
      questionsPerTopic: this.config.questionsPerTopic,
      totalQuestions: this.config.totalQuestions,
      categoryWeights: this.config.categoryWeights,
      adaptiveDifficulty: this.config.difficulty === 'adaptive'
    };
  }

  // Update configuration
  updateConfig(newConfig: Partial<AIExamConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

// Export singleton instance
export const aiExamAdapter = new AIExamAdapter();
